import { isUndefined, pickBy, isEmpty } from 'lodash'
import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	LayoutDoneServiceSymbol,
	ILayoutDoneService,
	ViewerManagerStructureAPISymbol,
	CarmiInstance,
	StructureAPI,
	IStructureAPI,
	Props,
	IPropsStore,
	Experiments,
	ExperimentsSymbol,
	LoggerSymbol,
	ILogger,
} from '@wix/thunderbolt-symbols'

import type { CssOverrides } from '@wix/thunderbolt-commons'
import { ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { IUpdateLayoutAndPushApi } from '../types'
import { getRuntimeMeshOverridesManager } from '../services/runtimeMeshOverrides'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

type OperationState = {
	compId: string
	initialMeasures: {
		offsetTop: number
		offsetHeight: number
		isRotated: boolean
	}
}

export const UpdateLayoutAndPushApi = withDependencies(
	[
		BrowserWindowSymbol,
		ExperimentsSymbol,
		LoggerSymbol,
		LayoutDoneServiceSymbol,
		ViewerManagerStructureAPISymbol,
		CarmiInstance,
		StructureAPI,
		Props,
		MeasuresApiSymbol,
	],
	(
		window: NonNullable<BrowserWindow>,
		experiments: Experiments,
		logger: ILogger,
		layoutDoneService: ILayoutDoneService,
		structureViewerApi: ViewerAPI['structure'],
		{ meshRuntimeData: setter }: DSCarmi,
		structureApi: IStructureAPI,
		props: IPropsStore,
		measuresApi: IMeasuresApi
	): IUpdateLayoutAndPushApi => {
		const runtimeMeshOverridesManager = getRuntimeMeshOverridesManager()
		let operationState: OperationState | null = null

		const addMeshOverrides = (compId: string) => {
			const comp = structureViewerApi.getStructure(compId)
			const { layout: compLayout, parent: parentId } = comp
			const siblings = structureViewerApi.getStructure(parentId!).components || []
			const siblingToMarkAsAbsolute = siblings.filter((siblingId) => {
				const sibling = structureViewerApi.getStructure(siblingId)
				const { layout: siblingLayout } = sibling

				return siblingLayout.y! < compLayout.y!
			})

			setter.setAbsoluteComps(siblingToMarkAsAbsolute)
			props.update({
				bodyClassUpdater: { withRuntimeMesh: true },
			})
		}

		const setMeshOverrides = (compId: string) => {
			runtimeMeshOverridesManager.clearAllItemsCssOverrides(window)
			addMeshOverrides(compId)
		}

		const clearMeshOverrides = () => {
			setter.setAbsoluteComps([])
			props.update({
				bodyClassUpdater: { withRuntimeMesh: false },
			})
		}

		const isMeasuresChanged = (element: HTMLElement, initialMeasures: OperationState['initialMeasures']) => {
			return (
				element.offsetTop !== initialMeasures.offsetTop || element.offsetHeight !== initialMeasures.offsetHeight
			)
		}

		const clear = () => {
			operationState = null
			clearMeshOverrides()
		}

		let waitForMeshOverridesApplied: Promise<void>

		const updateAndPushStart: IUpdateLayoutAndPushApi['updateAndPushStart'] = (compId) => {
			if (operationState === null) {
				const node = measuresApi.getCompElement(compId)
				const rotatedWrapperSelector = `[data-mesh-id="${compId}-rotated-wrapper"]`
				const isRotated = !!window.document.querySelector(rotatedWrapperSelector)

				if (node) {
					operationState = {
						compId,
						initialMeasures: {
							offsetTop: node.offsetTop,
							offsetHeight: node.offsetHeight,
							isRotated,
						},
					}
					layoutDoneService.enableLayoutDone(false)
					setMeshOverrides(compId)
					waitForMeshOverridesApplied = new Promise<void>((resolve) =>
						layoutDoneService.registerToRenderDone(() => resolve(), { once: true })
					)
				}
			}
		}

		const getCssOverrides = (
			compId: string,
			isRotated: boolean,
			runtimeLayout: { y: number; height: number }
		): Record<string, CssOverrides> => {
			const { y: offsetTop, height } = runtimeLayout

			const compIdSelector = `#${measuresApi.getCompIdSelectorForMesh(compId)}`
			const rotatedWrapperSelector = `[data-mesh-id="${compId}-rotated-wrapper"]`
			const selectorToCss: Record<string, CssOverrides> = {
				[compIdSelector]: {},
				[rotatedWrapperSelector]: {},
			}

			if (!isUndefined(height)) {
				selectorToCss[compIdSelector].height = `${height}px`
			}

			if (!isUndefined(offsetTop)) {
				if (isRotated) {
					selectorToCss[rotatedWrapperSelector]['margin-top'] = `${offsetTop}px`
				} else {
					selectorToCss[compIdSelector]['margin-top'] = `${offsetTop}px`
				}
			}

			return pickBy(selectorToCss, (value) => !isEmpty(value))
		}

		const updateAndPushUpdate: IUpdateLayoutAndPushApi['updateAndPushUpdate'] = (compId, runtimeLayout) => {
			const shouldIgnore = !operationState || operationState.compId !== compId

			if (shouldIgnore) {
				return
			}

			const selectorToCss = getCssOverrides(compId, operationState!.initialMeasures.isRotated, runtimeLayout)

			Object.keys(selectorToCss).forEach((selector) => {
				runtimeMeshOverridesManager.setItemCssOverrides(selectorToCss[selector], selector, window)
			})
		}

		const updateAndPushEnd: IUpdateLayoutAndPushApi['updateAndPushEnd'] = async (compId) => {
			const shouldIgnore = !operationState || operationState.compId !== compId

			if (shouldIgnore) {
				return
			}

			await waitForMeshOverridesApplied
			const node = measuresApi.getCompElement(compId)

			if (node && isMeasuresChanged(node, operationState!.initialMeasures)) {
				layoutDoneService.addToChangedComponents(compId)
				// wait for renderDone to remove the overrides together with the new mesh render
				const renderDoneCallbackId = layoutDoneService.registerToComponentsLayoutChange(
					() => {
						clear()
						layoutDoneService.unregisterToLayoutDone(renderDoneCallbackId)
					},
					{ once: false },
					true
				)
			} else {
				clear()
			}

			layoutDoneService.dispatchNextLayoutDoneBeforePaint()
			layoutDoneService.enableLayoutDone(true)
		}

		return {
			updateAndPushStart,
			updateAndPushUpdate,
			updateAndPushEnd,
		}
	}
)
